import store from '@polleverywhere/redux'
import { connect } from 'pwa-helpers/connect-mixin'
import { LitElement, html, internalProperty } from 'lit-element'
import { register } from '@polleverywhere/hmr-custom-element'
import { contextualize } from '@polleverywhere/cutlass'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import { peFetch, apiUrlFor } from '@polleverywhere/fetch'
import activityFactory from '@polleverywhere/graphql-models/lib/activityFactory'
import env from '@polleverywhere/core/lib/env'
import subscribe from '@polleverywhere/firehose_client'
import '@polleverywhere/pe-graphic'
import { outOfBandAuth } from '../../util/out_of_band_auth'
import destroySession from '../../util/destroy_session'
import '../../components/webex/home'
import { SUPPORT_URL } from '../../components/webex/home/constants'
import './style.scss'
import '../../../lib/components/webex/auth/'

store.addReducers({
  [profileSlice.name]: profileSlice.reducer
})

export default class Presenter extends connect(store)(LitElement) {
  static get tagName () { return 'app-webex' }

  @internalProperty({ type: String })
  login

  @internalProperty({ type: Object })
  channel

  constructor () {
    super()
    contextualize(this, this.tagName)
    // Override the theme. There may be a better way
    // but the current app options do not honor the passed
    // in theme
    document.querySelector('pe-application').theme = 'light'
  }

  stateChanged (rootState) {
    this.login = rootState[profileSlice.name].user?.login
  }

  updated (changedProps) {
    if (changedProps.has('login') && this.login) {
      startListening.call(this)
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    stopListening.call(this)
  }

  createRenderRoot () { return this }

  render () {
    return html`
      <div class=${bem('header')}>
        <pe-graphic type='illustration' name='logotype-blue'></pe-graphic>
      </div>

      <div class=${bem('content')}>
        ${this.login
          ? html`
            <pe-webex-home @click:logout=${handleLogout}></pe-webex-home>
            `
          : html`
            <pe-webex-auth @click:button=${handleAuth}>
              <a @click=${openSupport} href=${SUPPORT_URL}
                 target='_blank'>
                 Getting started with Poll Everywhere and Webex
              </a>
            </pe-webex-auth>
            `
        }
      </div>
    `
  }
}

function openSupport (e) {
  e.preventDefault()
  window.open(SUPPORT_URL, '', 'width=1400, height=900')
}

async function handleAuth (e) {
  try {
    await outOfBandAuth()
    window.location.reload()
  } catch (e) {
    console.error('OOB ERROR', e)
  }
}

async function startListening () {
  if (!this.login) return

  let firehoseToken = null

  // Function to get a new firehose token
  const preflight = async () => {
    const response = await peFetch(apiUrlFor('/api/firehose_tokens/current_activity', { presenter: this.login }))
    const data = await response.json()
    firehoseToken = data.firehose_token
  }

  // Run initially for first token
  await preflight()

  // Actually subscribe
  this.channel = subscribe(`users/${this.login}/activity/current.json`, {
    host: env.get('firehose_url').toString(),
    ssl: env.get('firehose_ssl'),
    params: () => { return { firehose_token: firehoseToken } },
    preflight
  })

  this.channel.on('message', setActivity.bind(this))
}

function stopListening () {
  this.channel?.unsubscribe()
  this.channel = null
  stopActivity(this.querySelector('pe-webex-home')?.activity)
}

async function setActivity (data) {
  const home = this.querySelector('pe-webex-home')
  if (!home) return

  stopActivity(home.activity)
  if (Object.keys(data).length === 0) {
    home.activity = null
  } else {
    const activity = activityFactory({
      type: data.type,
      participant_key: data.uid
    })
    try {
      await activity.fetch()
      activity.unset('participant_key')
      await activity.fetch()
      home.activity = activity
      if (activity.get('type') === 'competition') {
        activity.set({ display_state: 'welcome' })
      } else {
        // for competitions we don't need to listen for responses
        startActivity(home.activity)
      }
    } catch (err) {
      console.error(err)
    }
  }
}

async function handleLogout () {
  await destroySession()
}

// helper methods for listening to activity's update on firehose
function startActivity (activity) {
  if (!activity) return

  if (activity.type() === 'discourse') {
    activity.liveUpdate()
  } else {
    activity.instance.start()
  }
}

function stopActivity (activity) {
  if (!activity) return

  if (activity.type() === 'discourse') {
    activity.stopLiveUpdate()
  } else {
    activity.instance.stop()
  }
}

const bem = bemBlock(Presenter.tagName)
register(module, Presenter.tagName, Presenter)
